/**
 * UserMessage.tsx
 *
 * Renders a single user message bubble. Handles two variants:
 *   - Current user: right-aligned blue bubble with profile avatar
 *   - Other user (shared session): left-aligned bubble with sender attribution
 *
 * Preserves all class names, aria-labels, and DOM structure from the original
 * ChatMessage.tsx so the M-suite preservation gates (message--other-user,
 * message-sender-name) remain green.
 */

import type { ChatMessage as ChatMessageType, AuthUser } from "../../types/index.js";
import UserAvatar from "../UserAvatar.js";
import { formatTimestamp, formatTimeOnly } from "./AssistantMessage.parts.js";

export interface UserMessageProps {
	message: ChatMessageType;
	/** The authenticated user — used to determine current-user vs other-user branch. */
	user?: AuthUser;
}

/**
 * Renders a user message. Determines the current-user vs other-user branch
 * by checking whether senderDisplayName is set and whether userId matches
 * the authenticated user. Falls back safely in solo sessions (no
 * senderDisplayName populated → always current-user branch).
 */
export default function UserMessage({ message, user }: UserMessageProps) {
	// Messages from other team members get distinct left-aligned styling with sender attribution.
	// Note: senderDisplayName is set for ALL user messages in team sessions (including the current
	// user's own messages), so we cannot use it alone to distinguish. We treat a message as the
	// current user's own message only when there is NO senderDisplayName, OR when we can positively
	// confirm the userId matches the authenticated user. In all other cases (senderDisplayName is
	// set but we cannot confirm it is the current user) we render as other-user — this is safe for
	// solo sessions because senderDisplayName is never populated there.
	const isCurrentUser =
		!message.senderDisplayName ||
		Boolean(message.userId && user?.zohoUserId && message.userId === user.zohoUserId);
	const isOtherUser = !isCurrentUser;

	if (isOtherUser) {
		const senderName = message.senderDisplayName ?? "Team member";
		const timeStr = formatTimeOnly(message.timestamp);
		return (
			<div className="message message--other-user">
				<div className="message-user-wrapper">
					<div
						className="message-bubble"
						aria-label={`${senderName} at ${timeStr}`}
					>
						<span className="message-sender-name" data-testid="message-sender-name">
							{message.senderDisplayName}
						</span>
						{message.content}
					</div>
					<span className="message-timestamp">
						<time aria-hidden="true" dateTime={message.timestamp.toISOString()}>
							{formatTimestamp(message.timestamp)}
						</time>
						<span className="sr-only">{formatTimestamp(message.timestamp)}</span>
					</span>
				</div>
			</div>
		);
	}

	// Determine the sender name for the aria-label
	const currentUserName = user?.displayName ?? "You";
	const ownTimeStr = formatTimeOnly(message.timestamp);
	return (
		<div className="message message--user">
			{/* The wrapper is the flex item — set 85% max-width here so the
			    bubble's own max-width: 100% can resolve against a non-circular
			    reference, breaking the shrink-wrapping loop. */}
			<div className="message-user-wrapper">
				<div
					className="message-bubble"
					aria-label={`${currentUserName} at ${ownTimeStr}`}
				>
					{message.content}
				</div>
				<span className="message-timestamp">
					<time aria-hidden="true" dateTime={message.timestamp.toISOString()}>
						{formatTimestamp(message.timestamp)}
					</time>
					<span className="sr-only">{formatTimestamp(message.timestamp)}</span>
				</span>
			</div>
			{user && <UserAvatar user={user} size={28} />}
		</div>
	);
}
